/**
 * Hero — the first impression.
 *
 * Keeps the ContainerScroll animation + existing assets (background, dash
 * underline, admin dashboard screenshot). Refreshes the headline and
 * sub-copy to lead with the real differentiator — a business that keeps
 * its own books — and adds staggered entrance motion.
 */
import { motion } from "framer-motion";
import Star from "@/assets/star.svg";
import backgroundSvg from "@/assets/background.svg";
import Dash from "@/assets/dash.svg";
import { Button } from "../ui/button";
import { ArrowRight } from "lucide-react";
import AdminDashboard from "@/assets/dashboard/admin-dashboard.png";
import { ContainerScroll } from "../ui/container-scroll-animation";
import { Link } from "react-router-dom";

const Hero = () => {
  return (
    <div className="relative overflow-hidden pt-80">
      <div className="absolute inset-0 -z-10">
        <img
          src={backgroundSvg}
          alt="Background Pattern"
          className="h-full w-full object-cover"
        />
      </div>
      <div className="max-md:mt-12 md:py-10">
        <ContainerScroll
          titleComponent={
            <div className="container mx-auto flex flex-col gap-8 items-center min-h-screen px-4 py-16 text-center">
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="border border-[#4CAF50] text-[#4CAF50] bg-[#4CAF50]/10 rounded-full px-5 py-2 flex items-center gap-2"
              >
                <img src={Star} alt="star" />
                <p className="text-sm font-semibold tracking-wide">
                  AFRICA'S BUSINESS & REVENUE OPERATING SYSTEM
                </p>
              </motion.div>

              {/* Headline */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="max-w-6xl mx-auto"
              >
                <h1 className="text-5xl md:text-7xl font-bold leading-tight text-gray-900">
                  Manage Operations.
                </h1>

                <h1 className="text-5xl md:text-7xl font-bold leading-tight text-gray-900">
                  Automate Compliance.
                </h1>

                <h1 className="text-5xl md:text-7xl font-bold leading-tight">
                  Grow Revenue with{" "}
                  <span className="text-[#4CAF50] relative inline-block">
                    Eaze
                    <img
                      src={Dash}
                      alt="dash"
                      className="absolute left-1/2 -translate-x-1/2 -bottom-5"
                    />
                  </span>
                </h1>
              </motion.div>

              {/* Sub Headline */}
              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-lg md:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed"
              >
                ERP, Accounting, Procurement, CRM, Inventory, Revenue Assurance
                and Industry Solutions built for Africa. From growing businesses
                to multi-company enterprises, Eaze gives you complete control
                over operations, finance, compliance and growth.
              </motion.p>

              {/* Feature Pills */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="flex flex-wrap justify-center gap-3"
              >
                {[
                  "ERP Suite",
                  "Revenue Assurance",
                  "AI Analytics",
                  "Industry Cloud",
                  "Multi-Business",
                  "Offline First",
                ].map((item) => (
                  <span
                    key={item}
                    className="px-4 py-2 rounded-full border border-[#4CAF50]/20 bg-white/80 text-sm font-medium text-gray-700 shadow-sm"
                  >
                    {item}
                  </span>
                ))}
              </motion.div>

              {/* CTA */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col md:flex-row items-center gap-4"
              >
                <Link to="/register">
                  <Button className="bg-gradient-to-r from-[#51BB55] to-[#45A049] text-white px-8 py-6 text-base shadow-lg hover:scale-[1.02] transition-all">
                    Start Now
                  </Button>
                </Link>

                {/* <Link to="/book-demo">
                  <Button
                    variant="outline"
                    className="px-8 py-6 text-base border-[#4CAF50] text-[#4CAF50]"
                  >
                    Book Demo
                  </Button>
                </Link> */}

                <Link to="/contact-sales">
                  <Button variant="ghost" className="px-8 py-6 text-base">
                    Talk to Sales
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </motion.div>

              {/* Trust Statement */}
              {/* <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-sm text-gray-500"
              >
                Built for Retail, wholesale, Construction, Healthcare, Logistics,
                Manufacturing, Oil & Gas and Government.
              </motion.div> */}
            </div>
          }
        >
          <img
            src={AdminDashboard}
            alt="Eaze admin dashboard"
            className="object-fill h-full w-full"
            draggable={false}
          />
        </ContainerScroll>
      </div>
    </div>
  );
};

export default Hero;
